import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGym } from '../contexts/GymContext';
import { AlvoLogo } from '../components/AlvoLogo';
import { GymSelector } from '../components/GymSelector';
import { AREAS, getAreaEntryPath } from '../config/areas';
import { LogOut, ChevronRight } from 'lucide-react';

export const HubPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { selectedGym, loading: gymLoading } = useGym();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: 'var(--app-background)', color: 'var(--app-text-primary)' }}
      data-testid="hub-page"
    >
      {/* Papel de parede sutil (base), fixo atrás do conteúdo */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 bg-no-repeat bg-cover bg-bottom opacity-40"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ''}/hub-bg.jpg)` }}
      />
      {/* Vinheta para suavizar e manter os cards legíveis */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{ background: 'linear-gradient(to bottom, var(--app-background) 0%, color-mix(in srgb, var(--app-background) 55%, transparent) 45%, color-mix(in srgb, var(--app-background) 70%, transparent) 100%)' }}
      />
      <div className="relative z-10 flex flex-col flex-1">
      {/* Top bar */}
      <header
        className="flex items-center justify-between gap-3 px-4 sm:px-8 py-4 border-b"
        style={{ borderColor: 'var(--app-border)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <AlvoLogo size={38} />
          <div className="min-w-0">
            <h1 className="text-2xl font-heading font-black uppercase tracking-tight leading-none" style={{ color: 'var(--app-text-primary)' }}>
              Alvo
            </h1>
            <p className="hidden sm:block text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--app-text-secondary)' }}>
              Performance Empresarial
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {!gymLoading && selectedGym && (
            <div className="hidden sm:block w-56">
              <GymSelector />
            </div>
          )}
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-black font-bold text-sm shrink-0"
              style={{ background: `linear-gradient(to bottom right, var(--color-primary), var(--color-secondary))` }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <span className="hidden md:block text-sm font-medium truncate max-w-[140px]" style={{ color: 'var(--app-text-primary)' }}>
              {user?.name}
            </span>
          </div>
          <button
            data-testid="hub-logout"
            onClick={handleLogout}
            className="p-2.5 rounded-xl border transition-colors"
            style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-secondary)' }}
            title="Sair"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Hub content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-8 py-10 sm:py-16">
        <div className="w-full max-w-5xl">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-4xl sm:text-5xl font-heading font-black tracking-tight" style={{ color: 'var(--app-text-primary)' }}>
              Bem-vindo ao <span style={{ color: 'var(--color-primary)' }}>ALVO</span>
            </h2>
            <p className="mt-3 text-base sm:text-lg" style={{ color: 'var(--app-text-secondary)' }}>
              Escolha a área que deseja acessar.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {AREAS.map((area, idx) => {
              const Icon = area.icon;
              const color = area.color;
              return (
                <button
                  key={area.id}
                  data-testid={`hub-card-${area.id}`}
                  onClick={() => navigate(getAreaEntryPath(area))}
                  className="group relative text-left rounded-2xl border p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                  style={{
                    backgroundColor: 'var(--app-card-bg)',
                    borderColor: 'var(--app-border)',
                    animation: `hubFadeUp 0.5s ease ${idx * 0.06}s both`,
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = `color-mix(in srgb, ${color} 55%, transparent)`;
                    e.currentTarget.style.boxShadow = `0 10px 40px color-mix(in srgb, ${color} 14%, transparent)`;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = 'var(--app-border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-105"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`,
                      border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
                      color: color,
                    }}
                  >
                    <Icon size={34} strokeWidth={1.75} />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-xl font-heading font-bold" style={{ color: 'var(--app-text-primary)' }}>
                      {area.label}
                    </h3>
                    <ChevronRight
                      size={18}
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                      style={{ color: color }}
                    />
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed" style={{ color: 'var(--app-text-secondary)' }}>
                    {area.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </main>
      </div>

      <style>{`
        @keyframes hubFadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default HubPage;
